import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translation dictionary — keep all UI strings here (translation-ready)
const T = {
  fr: {
    loading: "Chargement…",
    notFound: "Événement introuvable",
    backHome: "← Retour à l'accueil",
    eventDate: "Date",
    eventTime: "Heure",
    eventVenue: "Lieu",
    pickYourTickets: "Choisis tes billets",
    soldOut: "ÉPUISÉ",
    remaining: "places restantes",
    quantity: "Quantité",
    subtotal: "Sous-total",
    total: "Total",
    yourInfo: "Tes informations",
    fullName: "Nom complet",
    email: "Courriel",
    phone: "Téléphone (optionnel)",
    proceedPay: "Procéder au paiement",
    processing: "Traitement…",
    cancelled: "Paiement annulé. Aucun montant n'a été prélevé.",
    cancelledTitle: "Paiement annulé",
    cancelledMsg: "Tu peux retenter à tout moment.",
    selectAtLeastOne: "Sélectionne au moins un billet",
    emailRequired: "Courriel obligatoire",
    nameRequired: "Nom obligatoire",
    securePay: "Paiement sécurisé via Stripe",
    refundPolicy: "Politique de remboursement : voir conditions",
    languageToggle: "EN",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
  en: {
    loading: "Loading…",
    notFound: "Event not found",
    backHome: "← Back to home",
    eventDate: "Date",
    eventTime: "Time",
    eventVenue: "Venue",
    pickYourTickets: "Pick your tickets",
    soldOut: "SOLD OUT",
    remaining: "spots left",
    quantity: "Quantity",
    subtotal: "Subtotal",
    total: "Total",
    yourInfo: "Your details",
    fullName: "Full name",
    email: "Email",
    phone: "Phone (optional)",
    proceedPay: "Proceed to payment",
    processing: "Processing…",
    cancelled: "Payment cancelled. You weren't charged.",
    cancelledTitle: "Payment cancelled",
    cancelledMsg: "You can try again any time.",
    selectAtLeastOne: "Select at least one ticket",
    emailRequired: "Email required",
    nameRequired: "Name required",
    securePay: "Secure payment via Stripe",
    refundPolicy: "Refund policy: see terms",
    languageToggle: "FR",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
};

export default function TicketingEventPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState("fr");
  const [event, setEvent] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { ticket_type_id: qty }
  const [seatmap, setSeatmap] = useState(null);
  const [seatStatus, setSeatStatus] = useState({});
  const [selectedSeats, setSelectedSeats] = useState([]); // [seat_id]
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const cancelled = searchParams.get("cancelled") === "1";
  const t = T[lang];

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/ticketing/public/${slug}`);
        setEvent(r.data.event);
        setTypes(r.data.ticket_types);
        try {
          const sm = await axios.get(`${API}/ticketing/public/${slug}/seatmap`);
          if (sm.data.seatmap) {
            setSeatmap(sm.data.seatmap);
            setSeatStatus(sm.data.status || {});
          }
        } catch (e) { /* pas de plan = mode quantité */ }
      } catch (e) {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const toggleSeat = (seatId) => {
    if (seatStatus[seatId]) return; // occupé
    setSelectedSeats((s) => s.includes(seatId) ? s.filter((x) => x !== seatId) : [...s, seatId]);
  };

  const setQty = (tid, qty) => {
    setCart((c) => {
      const nc = { ...c };
      if (!qty || qty <= 0) delete nc[tid];
      else nc[tid] = qty;
      return nc;
    });
  };

  const total = Object.entries(cart).reduce((sum, [tid, qty]) => {
    const tt = types.find((x) => x.id === tid);
    return sum + (tt ? tt.price_cad * qty : 0);
  }, 0);
  const hasItems = Object.keys(cart).length > 0;

  // V2 — prix des sièges sélectionnés
  const seatPrice = (seatId) => {
    if (!seatmap) return 0;
    const si = parseInt(seatId.split("-")[0]);
    const sec = seatmap.sections[si];
    return sec ? (sec.price_cad || 0) : 0;
  };
  const seatTotal = selectedSeats.reduce((s, id) => s + seatPrice(id), 0);
  const seatMode = Boolean(seatmap);
  const canCheckout = seatMode ? selectedSeats.length > 0 : hasItems;

  const checkout = async () => {
    if (!canCheckout) { toast.error(t.selectAtLeastOne); return; }
    if (!customer.email) { toast.error(t.emailRequired); return; }
    if (!customer.name) { toast.error(t.nameRequired); return; }
    setBusy(true);
    try {
      const body = {
        customer_email: customer.email,
        customer_name: customer.name,
        customer_phone: customer.phone,
        language: lang,
        origin_url: window.location.origin,
      };
      if (seatMode) body.seat_ids = selectedSeats;
      else body.items = Object.entries(cart).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
      const r = await axios.post(`${API}/ticketing/public/${slug}/checkout`, body);
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center" data-testid="ticketing-loading">{t.loading}</div>;
  }
  if (!event) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl font-bold" data-testid="event-not-found">{t.notFound}</div>
        <Link to="/" className="text-amber-400 hover:underline">{t.backHome}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ticketing-event-page">
      {/* Top bar with language toggle */}
      <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="text-xs tracking-[0.3em] text-amber-400 hover:text-amber-300">KAIZEN SYSTEM</Link>
        <button
          onClick={() => setLang(lang === "fr" ? "en" : "fr")}
          className="text-xs border border-zinc-700 px-3 py-1.5 rounded-full hover:border-amber-500 hover:text-amber-400 transition"
          data-testid="lang-toggle-btn"
        >
          {t.languageToggle}
        </button>
      </div>

      {/* Cancelled banner */}
      {cancelled && (
        <div className="max-w-5xl mx-auto px-4 mt-4">
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-xl p-4" data-testid="cancelled-banner">
            <div className="text-sm font-bold text-amber-300">⚠️ {t.cancelledTitle}</div>
            <div className="text-xs text-amber-200/80 mt-1">{t.cancelled} {t.cancelledMsg}</div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-10 pb-8">
        <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{lang === "fr" ? "BILLETTERIE OFFICIELLE" : "OFFICIAL TICKETING"}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="event-title">
          {lang === "fr" ? event.title_fr : event.title_en}
        </h1>
        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          <Detail label={t.eventDate} value={event.date} />
          <Detail label={t.eventTime} value={event.time || "—"} />
          <Detail label={t.eventVenue} value={`${event.venue_name || ""}${event.venue_city ? " · " + event.venue_city : ""}`} />
        </div>
        {(lang === "fr" ? event.description_fr : event.description_en) && (
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-3xl" data-testid="event-description">
            {lang === "fr" ? event.description_fr : event.description_en}
          </p>
        )}
      </div>

      {/* Ticket selection */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{seatMode ? (lang === "fr" ? "CHOISIS TES SIÈGES" : "PICK YOUR SEATS") : t.pickYourTickets.toUpperCase()}</div>
        {seatMode ? (
          <SeatPicker
            seatmap={seatmap}
            status={seatStatus}
            selected={selectedSeats}
            onToggle={toggleSeat}
            lang={lang}
          />
        ) : (
          <div className="space-y-3" data-testid="ticket-types-list">
            {types.map((tt) => (
              <TicketRow
                key={tt.id}
                type={tt}
                lang={lang}
                qty={cart[tt.id] || 0}
                onChange={(q) => setQty(tt.id, q)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Customer + Total + CTA */}
        {canCheckout && (
          <div className="mt-10 border border-amber-500/30 rounded-2xl p-6 bg-gradient-to-br from-amber-500/10 to-amber-500/5" data-testid="checkout-block">
            <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{t.yourInfo.toUpperCase()}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t.fullName} *</Label>
                <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="bg-black border-zinc-800" data-testid="customer-name-input" />
              </div>
              <div>
                <Label className="text-xs">{t.email} *</Label>
                <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-black border-zinc-800" data-testid="customer-email-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t.phone}</Label>
                <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="bg-black border-zinc-800" data-testid="customer-phone-input" />
              </div>
            </div>

            <div className="mt-5 border-t border-amber-500/20 pt-4">
              {seatMode && (
                <div className="mb-3 text-xs text-zinc-300" data-testid="selected-seats-summary">
                  {lang === "fr" ? "Sièges :" : "Seats:"}{" "}
                  <span className="text-amber-300 font-semibold">{selectedSeats.length}</span> ({selectedSeats.map((s) => s.split("-")[1]).join(", ")})
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">{t.total}</span>
                <span className="text-3xl font-bold text-amber-300" data-testid="cart-total">{(seatMode ? seatTotal : total).toFixed(2)} $ CAD</span>
              </div>
              <Button
                onClick={checkout}
                disabled={busy}
                className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-6 text-base"
                data-testid="checkout-btn"
              >
                {busy ? t.processing : t.proceedPay} →
              </Button>
              <div className="mt-3 text-[10px] text-zinc-500 text-center">🔒 {t.securePay} · {t.refundPolicy}</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-900 py-8 mt-10">
        <div className="max-w-5xl mx-auto px-4 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/50">
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className="text-zinc-100 font-semibold mt-1">{value}</div>
    </div>
  );
}

function TicketRow({ type, lang, qty, onChange, t }) {
  const soldOut = type.sold_out;
  return (
    <div
      className={`border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${soldOut ? "border-zinc-900 bg-zinc-950/30 opacity-50" : "border-zinc-800 bg-zinc-950/50 hover:border-amber-500/30 transition"}`}
      data-testid={`ticket-type-${type.code}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="text-lg font-bold text-zinc-100">{lang === "fr" ? type.name_fr : type.name_en}</h3>
          {type.seats_per_unit > 1 && (
            <span className="text-[10px] tracking-[0.2em] uppercase text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">
              {type.seats_per_unit} {lang === "fr" ? "places" : "seats"}
            </span>
          )}
        </div>
        <div className="text-sm text-zinc-400 mt-1">{lang === "fr" ? type.description_fr : type.description_en}</div>
        <div className="text-xs mt-2 flex items-center gap-2">
          <span className="text-amber-300 font-bold text-base">{type.price_cad.toFixed(2)} $</span>
          {!soldOut && (
            <span className="text-zinc-500">· {type.remaining} {t.remaining}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {soldOut ? (
          <span className="text-red-400 font-bold text-sm tracking-[0.2em]" data-testid={`sold-out-${type.code}`}>{t.soldOut}</span>
        ) : type.on_sale === false ? (
          <span className="text-amber-300 font-semibold text-xs tracking-wide" data-testid={`not-on-sale-${type.code}`}>{type.not_on_sale_reason}</span>
        ) : (
          <>
            <button
              onClick={() => onChange(Math.max(0, qty - 1))}
              className="w-9 h-9 rounded-full border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-400 transition"
              data-testid={`qty-minus-${type.code}`}
            >−</button>
            <input
              value={qty}
              onChange={(e) => {
                const n = Number(e.target.value || 0);
                onChange(Math.max(0, Math.min(type.remaining, type.max_per_order || 99, n)));
              }}
              className="w-14 text-center bg-black border border-zinc-800 rounded-lg py-2 text-zinc-100"
              data-testid={`qty-input-${type.code}`}
            />
            <button
              onClick={() => onChange(Math.min(type.remaining, type.max_per_order || 99, qty + 1))}
              disabled={type.on_sale === false}
              className="w-9 h-9 rounded-full border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-400 transition"
              data-testid={`qty-plus-${type.code}`}
            >+</button>
          </>
        )}
      </div>
    </div>
  );
}


function SeatPicker({ seatmap, status, selected, onToggle, lang }) {
  return (
    <div className="space-y-6" data-testid="seat-picker">
      {/* Scène */}
      <div className="text-center">
        <div className="inline-block bg-zinc-800 text-zinc-300 text-[10px] tracking-[0.4em] uppercase px-10 py-2 rounded-md">
          {seatmap.stage_label}
        </div>
      </div>

      {seatmap.sections.map((sec) => (
        <div key={sec.id} className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40" data-testid={`seat-section-${sec.id}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: sec.color }} />
              <span className="text-sm font-bold text-zinc-100">{sec.name}</span>
              {sec.type === "vip" && (
                <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5">VIP</span>
              )}
            </div>
            <span className="text-amber-300 font-bold text-sm">{(sec.price_cad || 0).toFixed(2)} $</span>
          </div>
          <div className="space-y-1.5 overflow-x-auto">
            {sec.rows.map((row) => (
              <div key={row.label} className="flex items-center gap-1.5 justify-center min-w-max">
                <span className="w-5 text-[10px] text-zinc-600 text-right shrink-0">{row.label}</span>
                {row.seats.map((seat) => {
                  const st = status[seat.id];
                  const isSel = selected.includes(seat.id);
                  const occupied = Boolean(st);
                  return (
                    <button
                      key={seat.id}
                      onClick={() => onToggle(seat.id)}
                      disabled={occupied}
                      title={`${sec.name} ${seat.label}${occupied ? (lang === "fr" ? " (pris)" : " (taken)") : ""}`}
                      data-testid={`seat-${seat.id}`}
                      className={`w-6 h-6 rounded text-[9px] font-semibold shrink-0 transition ${
                        occupied
                          ? "bg-zinc-800 text-zinc-700 cursor-not-allowed"
                          : isSel
                          ? "bg-amber-400 text-black scale-110"
                          : "text-white hover:scale-110"
                      }`}
                      style={!occupied && !isSel ? { backgroundColor: sec.color } : undefined}
                    >
                      {seat.number}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Légende */}
      <div className="flex items-center justify-center gap-4 text-[10px] text-zinc-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> {lang === "fr" ? "Sélectionné" : "Selected"}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-800" /> {lang === "fr" ? "Occupé" : "Taken"}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600" /> {lang === "fr" ? "Disponible" : "Available"}</span>
      </div>
    </div>
  );
}
